/**
 * `/imports/new` — state A, or the Starter gate where the dropzone would be.
 *
 * Choosing a file is an event and the hash, the `POST` and the `PUT` are effects, so a static
 * render reaches neither: U79's flow drives them through the file chooser.
 */
import { useState, type ReactNode } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";

import { Surface } from "@packagepage/ui";
import { IMPORT_MAX_BYTES, planRank } from "@packagepage/shared";

import { problemOf } from "../api/client.js";
import { uploadImportFile, type UploadPhase } from "../api/import-upload.js";
import { api } from "../api/procedures.js";
import { ImportGate, ImportUpload, CAPS_SENTENCE } from "./ImportStates.js";
import { IMPORT_TIER } from "./import-wizard.js";

export function ImportNew(): ReactNode {
  const navigate = useNavigate();
  const org = useQuery({
    queryKey: ["orgs", "current"],
    queryFn: () => api.currentOrg(),
  });
  const [phase, setPhase] = useState<UploadPhase | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);

  const upload = useMutation({
    mutationFn: (file: File) => {
      setRefusal(null);
      if (file.size > IMPORT_MAX_BYTES) {
        throw new Error(`That file is over the limit. ${CAPS_SENTENCE}.`);
      }
      return uploadImportFile(file, setPhase);
    },
    onSuccess: (importId) => navigate({ to: "/imports/$importId", params: { importId } }),
    onError: (error) => {
      setPhase(null);
      setRefusal(
        problemOf(error)?.detail ??
          (error instanceof Error ? error.message : "The upload did not finish."),
      );
    },
  });

  if (org.isPending) return <p data-screen-label="Import loading">Loading…</p>;
  if (org.error !== null) {
    return (
      <Surface tone="warn" emphasis="filled">
        <p data-screen-label="Import">
          {problemOf(org.error)?.detail ??
            "We could not load your plan. Reload to try again."}
        </p>
      </Surface>
    );
  }
  if (planRank(org.data.plan) < planRank(IMPORT_TIER)) return <ImportGate />;

  return (
    <ImportUpload
      onPick={(file) => upload.mutate(file)}
      phase={upload.isPending ? (phase ?? "hashing") : null}
      refusal={refusal}
    />
  );
}
